/**
 * ManualClockDialog — Admin-only modal to force a Clock In or Clock Out
 * for any employee, bypassing geo-fencing / selfie / time checks.
 *
 * Records a full audit trail (admin_id, admin_override=true, admin_notes).
 * Backed by POST /api/admin/clock/in and POST /api/admin/clock/out.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { AlertTriangle, CheckCircle, Clock, LogIn, LogOut } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Format a Date as the string expected by <input type="datetime-local">,
 * i.e. "YYYY-MM-DDTHH:mm" in *local* time (no timezone suffix).
 */
function toDateTimeLocalValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export default function ManualClockDialog({
  open,
  onOpenChange,
  employee,          // { id, name, is_on_shift }
  defaultAction,     // 'in' | 'out' | undefined (auto)
  shiftId,           // optional — pin to a specific shift
  onSuccess,         // optional callback after success
}) {
  const isOnShift = !!employee?.is_on_shift;
  // Auto pick action from current status, unless caller overrides
  const initialAction = defaultAction || (isOnShift ? 'out' : 'in');

  const [action, setAction] = useState(initialAction);
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [customTime, setCustomTime] = useState(toDateTimeLocalValue(new Date()));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset local state whenever the dialog is (re)opened for a different employee/action
  useEffect(() => {
    if (open) {
      setAction(defaultAction || (employee?.is_on_shift ? 'out' : 'in'));
      setUseCustomTime(false);
      setCustomTime(toDateTimeLocalValue(new Date()));
      setNotes('');
      setSubmitting(false);
    }
  }, [open, employee?.id, employee?.is_on_shift, defaultAction]);

  if (!employee) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        employee_id: employee.id,
        notes: notes.trim() || null,
      };
      if (shiftId) payload.shift_id = shiftId;
      if (useCustomTime && customTime) {
        // Convert local datetime string -> ISO with local TZ offset
        payload.custom_time = new Date(customTime).toISOString();
      }

      const endpoint = action === 'in' ? '/admin/clock/in' : '/admin/clock/out';
      // 30s hard timeout so a slow backend can never leave the page stuck
      // behind the dialog overlay.
      await axios.post(`${API}${endpoint}`, payload, { timeout: 30000 });

      toast.success(
        action === 'in'
          ? `${employee.name} has been clocked IN manually.`
          : `${employee.name} has been clocked OUT manually.`,
      );
      onOpenChange(false);
      if (typeof onSuccess === 'function') onSuccess();
    } catch (err) {
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
      const detail = isTimeout
        ? 'The request took too long. Check Clock History to verify before retrying.'
        : (err?.response?.data?.detail || err?.message || 'Manual clock action failed');
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const nowLabel = format(new Date(), 'EEE, d MMM yyyy · HH:mm');

  return (
    <Dialog open={open} onOpenChange={(v) => onOpenChange(v)}>
      <DialogContent
        className="bg-card border-border rounded-sm max-w-md"
        data-testid="manual-clock-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Manual Clock {action === 'in' ? 'In' : 'Out'}
          </DialogTitle>
          <DialogDescription>
            Force a clock event for this employee. Geo-fencing and selfie checks are bypassed. The action is logged with your admin ID.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Employee context */}
          <div className="p-3 bg-accent/40 border border-border rounded-sm">
            <p className="text-sm font-medium" data-testid="manual-clock-employee-name">
              {employee.name}
            </p>
            <div className="mt-1.5 flex items-center gap-2">
              {isOnShift ? (
                <Badge className="status-active text-[10px] uppercase tracking-wider">
                  <CheckCircle className="w-3 h-3 mr-1" /> On Shift
                </Badge>
              ) : (
                <Badge className="bg-muted text-muted-foreground border-border text-[10px] uppercase tracking-wider">
                  Off Duty
                </Badge>
              )}
            </div>
          </div>

          {/* Action toggle — allow override in case status is stale */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Action</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAction('in')}
                data-testid="manual-clock-pick-in"
                className={
                  'flex items-center justify-center gap-2 p-2 rounded-sm border transition-colors text-sm ' +
                  (action === 'in'
                    ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300'
                    : 'bg-background border-border hover:border-emerald-500/40 text-muted-foreground')
                }
              >
                <LogIn className="w-4 h-4" /> Clock In
              </button>
              <button
                type="button"
                onClick={() => setAction('out')}
                data-testid="manual-clock-pick-out"
                className={
                  'flex items-center justify-center gap-2 p-2 rounded-sm border transition-colors text-sm ' +
                  (action === 'out'
                    ? 'bg-amber-500/15 border-amber-500/50 text-amber-300'
                    : 'bg-background border-border hover:border-amber-500/40 text-muted-foreground')
                }
              >
                <LogOut className="w-4 h-4" /> Clock Out
              </button>
            </div>
            {action === 'in' && isOnShift && (
              <p className="text-[11px] text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Employee appears to already be on shift. Backend may reject a duplicate clock-in.
              </p>
            )}
            {action === 'out' && !isOnShift && (
              <p className="text-[11px] text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Employee is not currently clocked in. Backend will look for the last open session.
              </p>
            )}
          </div>

          {/* Time selection */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Timestamp</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setUseCustomTime(false)}
                data-testid="manual-clock-time-now"
                className={
                  'p-2 rounded-sm border transition-colors text-sm ' +
                  (!useCustomTime
                    ? 'bg-primary/15 border-primary/50 text-primary'
                    : 'bg-background border-border hover:border-primary/40 text-muted-foreground')
                }
              >
                Now
              </button>
              <button
                type="button"
                onClick={() => setUseCustomTime(true)}
                data-testid="manual-clock-time-custom"
                className={
                  'p-2 rounded-sm border transition-colors text-sm ' +
                  (useCustomTime
                    ? 'bg-primary/15 border-primary/50 text-primary'
                    : 'bg-background border-border hover:border-primary/40 text-muted-foreground')
                }
              >
                Custom time
              </button>
            </div>
            {useCustomTime ? (
              <Input
                type="datetime-local"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
                className="bg-background border-border rounded-sm font-mono"
                data-testid="manual-clock-custom-time-input"
              />
            ) : (
              <p className="text-[11px] text-muted-foreground font-mono" data-testid="manual-clock-now-preview">
                {nowLabel}
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Reason / Notes <span className="text-muted-foreground/60">(optional)</span>
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Employee forgot to clock in. Verified attendance manually."
              className="bg-background border-border rounded-sm text-sm"
              rows={3}
              data-testid="manual-clock-notes"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="border-border rounded-sm"
            data-testid="manual-clock-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className={
              'rounded-sm ' +
              (action === 'in'
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-amber-600 hover:bg-amber-500 text-white')
            }
            data-testid="manual-clock-confirm"
          >
            {submitting
              ? 'Working…'
              : action === 'in'
                ? 'Confirm Clock In'
                : 'Confirm Clock Out'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
